import React, { Children, cloneElement, Component, PropTypes, PureComponent } from 'react';
import _ from 'underscore';
import SessionSelector from '../../components/SessionSelector';
import { pages } from '../../util/constants';
import { InverseButton, Icon, LoadingWrapper, PageHeader } from 'react-fast-ui';
import join from 'url-join';
import { tagEvent } from '../../util/tracking/localyticsHelper';
import PageTitle from '../../PageTitle';
import EmptyState from '../../common/components/EmptyState';
import { getCurrentSession } from '../../util/SessionUtils';
import TourneyModal from '../../common/components/modals/TourneyModal';
import { Link } from 'react-router';
import { getSortParams } from '../../util/Etc';
import TourneyTable from '../../components/tables/TourneyTable';
import standardIcons from '../../util/constants/icons';
import { connect } from 'react-redux';
import { getAllSessions, getAllTourneys, saveTourney } from '../actions/actionCreators';

export const TourneyBackLink = ({ tourney }) => (
  <Link to={tourney ? join('sessions', tourney.session.id) : 'sessions'}>
    <Icon name="chevron-left" className="mg-right-sm"/>
    {tourney ? tourney.session.name : 'Back'}
  </Link>
);

const ADD_TOURNEY_TITLE = 'Add Custom Tourney';
const SOURCE = { Source: 'SessionsPage' };
const NO_TOURNEYS = (
  <EmptyState iconProps={{ name: 'trophy' }}>
    No tourneys for this session.
  </EmptyState>
);
const INVALID_SESSION = (
  <EmptyState iconProps={{ name: 'trophy' }}>
    This URL is invalid!
  </EmptyState>
);

const mapStateToProps = store => ({
  sessions: _.toArray(store.sessionsById),
  usersById: store.usersById,
  tourneys: _.toArray(store.tourneysById),
  loadingTourneys: store.loadingTourneys,
  loadingSessions: store.loadingSessions
});

class Tourneys extends PureComponent {
  static propTypes = {
    params: PropTypes.object.isRequired,
    showAccountInfo: PropTypes.bool,
    tourneys: PropTypes.array.isRequired
  };

  state = {
    sortInfo: { attribute: 'start', desc: false }
  };

  componentDidMount() {
    this.fetchTourneys(this.props.params);
  }

  componentWillReceiveProps({ params }) {
    if (!_.isEqual(params, this.props.params)) {
      this.fetchTourneys(params);
    }
  }

  handleSort = sortInfo => {
    tagEvent('sortTourneys', () => ({ ...SOURCE, sortOn: sortInfo.attribute }));
    this.setState({ sortInfo }, () => this.fetchTourneys(this.props.params));
  };

  fetchTourneys(params) {
    const { sortInfo } = this.state;
    this.props.getTourneys({ ...params, ...getSortParams(sortInfo) });
  }

  render() {
    const { sortInfo } = this.state;
    const { showAccountInfo, tourneys } = this.props;

    return (
      <LoadingWrapper loading={!!this.props.loadingTourneys}>
        {
          tourneys === null || tourneys.length === 0 ?
            NO_TOURNEYS :
            <TourneyTable
              showAccountInfo={showAccountInfo} onSort={this.handleSort} sortInfo={sortInfo}
              tourneys={tourneys}
            />
        }
      </LoadingWrapper>
    );
  }
}

class SessionsPage extends PureComponent {
  static Tourneys = Tourneys;

  static contextTypes = {
    dao: PropTypes.object.isRequired,
    model: PropTypes.object.isRequired,
    router: PropTypes.object.isRequired,
  };

  static propTypes = {
    showAddTourney: PropTypes.bool,
    showAccountInfo: PropTypes.bool,
    tourneyParams: PropTypes.object,
    params: PropTypes.shape({
      sessionId: PropTypes.string
    }).isRequired
  };

  static defaultProps = {
    showAddTourney: true,
    showAccountInfo: true,
    tourneyParams: null
  };

  state = {
    saveNo: 0,
    promise: null,
    editingTourney: null,
  };

  componentDidMount() {
    this.fetchSessions();
  }

  componentWillReceiveProps({ params: { sessionId }, sessions }) {
    if (!sessionId || sessionId.length === 0 && sessions && sessions.length) {
      // if the user has unselected a session, go to the current
      this.selectCurrent(sessions);
    }
  }

  selectCurrent = sessions => this.handleSessionSelect(getCurrentSession(sessions), 'replace');

  fetchSessions() {
    const { dao } = this.context;
    this.props.dispatch(getAllSessions(dao, { sort: [ 'A|start', 'A|end' ] }));
  }

  getCurrentSession() {
    const { sessions } = this.props;
    const { params } = this.props;

    return _.findWhere(sessions, { id: params.sessionId });
  }

  handleSessionSelect = (session, method = 'push') => {
    const { router } = this.context;
    const { sessionId } = this.props.params;

    if (session && session.id !== sessionId) {
      tagEvent('selectSession', () => ({ ...SOURCE, session: session.id }));
      router[ method ](join('sessions', session.id));
    } else if (sessionId) {
      router[ method ]('sessions');
    }
  };

  cancelEditTourney = () => this.setState({ editingTourney: null });
  handleEditTourney = editingTourney => this.setState({ editingTourney });
  handleAddTourney = () => {
    const session = this.getCurrentSession();
    const { model } = this.context;

    this.handleEditTourney({
      session,
      start: session.start,
      end: session.end,
      status: 'Scheduled',
      accountId: model.getAccountId()
    });
  };

  handleSaveTourney = () => {
    const { editingTourney } = this.state;
    const { dao } = this.context;

    this.props.dispatch(saveTourney(dao, editingTourney));
    this.setState({ editingTourney: null });
  };

  render() {
    const { editingTourney, saveNo } = this.state;
    const { showAddTourney, showAccountInfo, children, tourneyParams, sessions, loadingSessions } = this.props;

    if (loadingSessions) {
      return <EmptyState>Loading sessions...</EmptyState>;
    }

    const session = this.getCurrentSession();

    return (
      <PageTitle title={session ? `${session.name} | Tourneys` : 'Tourneys'}>
        <div style={{ paddingBottom: 40 }}>
          <TourneyModal
            onSave={this.handleSaveTourney} onDelete={this.handleDelete} onChange={this.handleEditTourney}
            onClose={this.cancelEditTourney}
            tourney={editingTourney}
            open={!!editingTourney}
            title={ADD_TOURNEY_TITLE}
          />

          <PageHeader title="Tourneys" icon="trophy">
            { !showAddTourney ? null :
              <InverseButton disabled={!session} onClick={this.handleAddTourney}>
                <Icon name={standardIcons.add}/>
                <span className="mg-left-md">{ADD_TOURNEY_TITLE}</span>
              </InverseButton>
            }
          </PageHeader>
          <PageHeader.Subhead>
            <h4>
              <SessionSelector
                sessions={sessions} value={this.getCurrentSession()}
                onChange={this.handleSessionSelect}
              />
            </h4>
          </PageHeader.Subhead>

          {
            session ? (
                cloneElement(
                  Children.only(children), {
                    showAccountInfo,
                    getTourneys: (params) => this.props.dispatch(getAllTourneys(this.context.dao, params)),
                    loadingTourneys: this.props.loadingTourneys,
                    tourneys: this.props.tourneys,
                    params: {
                      ...tourneyParams,
                      sessionId: session.id,
                      _saveKey: saveNo
                    }
                  })
              ) : INVALID_SESSION
          }
        </div>
      </PageTitle>
    );
  }
}

export default connect(mapStateToProps)(SessionsPage);


// WEBPACK FOOTER //
// ./src/js/FastRecruit/components/SessionsPage.jsx